
import * as React from 'react';
import { DefaultButton } from 'office-ui-fabric-react';
import { BillingPlan, GetPlanConfig, GetPriceString } from '../common';
import { BadgeProps } from "./CurrentPlanBadge";
import { PlanCard } from './PlanCard';
import { ProductType } from '../../../../store/Tenant';

type RegularCardProps = {
    isCurrent: boolean;
    plan: BillingPlan;
    disabled: boolean;
    onChange: (plan: BillingPlan) => void;
    onSwitchTrial: (plan: BillingPlan) => void;
    onBuy: (plan: BillingPlan) => void;
    badgeProps: BadgeProps;
    hideManageSubscriptionButton?: boolean;
};

const ONCE_PLAN_OLD_PRICE = 2999;

export const RegularPlanCard = (props: RegularCardProps) => {
    const { plan, isCurrent, disabled, badgeProps, hideManageSubscriptionButton } = props;
    const onChange = React.useCallback(() => props.onChange(plan), [plan]);
    const onSwitchTrial = React.useCallback(() => props.onSwitchTrial(plan), [plan]);
    const onBuy = React.useCallback(() => props.onBuy(plan), [plan]);
    const config = React.useMemo(() => GetPlanConfig(plan), [plan]);

    const onPriceRender = React.useCallback(
        () => (
            <div className="plan-price-container">
                {plan.productType === ProductType.Once ? (
                    <div className="plan-price-column">
                        <div className="plan-price-container-row">
                            <div className="plan-price plan-old-price">{GetPriceString(ONCE_PLAN_OLD_PRICE, true)}</div>
                            <div className="plan-price-description">
                                <span>/year</span>
                            </div>
                        </div>
                        <div className="plan-price-container-row">
                            <div className="plan-price red-price">{GetPriceString(plan.price, true)}</div>
                            <div className="plan-price-description">
                                <span>/one time</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="plan-price">{GetPriceString(config.monthlyPrice)}</div>
                        <div className="plan-price-description">
                            <span>per user / month {config.text}</span>
                        </div>
                    </>
                )}
            </div>
        ),
        [plan, config]
    );

    const isChange = isCurrent && !badgeProps.isTrial;
    const isTrial = badgeProps.isTrial;
    const isExpired = badgeProps.isExpired;

    const onRenderButtons = React.useCallback(() => {
        const showSwitchTrial = !isCurrent && isTrial && !disabled;
        const showBuyNow = hideManageSubscriptionButton && isCurrent && (isTrial || isExpired);
        const showManageOrBuy = !hideManageSubscriptionButton;

        return (
            <>
                {showSwitchTrial && (
                    <DefaultButton
                        disabled={disabled}
                        onClick={onSwitchTrial}
                        text="Switch trial"
                    />
                )}

                {showBuyNow && (
                    <DefaultButton
                        disabled={disabled}
                        primary
                        onClick={onBuy}
                        text="Buy now"
                    />
                )}

                {showManageOrBuy && (
                    <DefaultButton
                        disabled={disabled}
                        primary
                        onClick={isChange ? onChange : onBuy}
                        text={isChange ? "Manage Subscription" : "Buy now"}
                    />
                )}
            </>
        );
    }, [isChange, disabled, isCurrent, isTrial, isExpired, hideManageSubscriptionButton, onBuy, onChange, onSwitchTrial]);

    return <PlanCard
        isCurrent={isCurrent}
        productType={plan.productType}
        name={plan.name}
        features={plan.featuresDescriptions}
        description={plan.description}
        iconName={plan.iconName}
        badgeProps={badgeProps}
        disabled={disabled}
        onPriceRender={onPriceRender}
        onRenderButtons={onRenderButtons}
    />;
}
